import { INTEGRATIONS_TYPES } from 'config/integrationTypes';
import { SOCKET_EVENTS } from 'config/socketEvents';
import socketURLGenerator from 'library/helpers/socketURLGenerator';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { accessLogsActions } from 'reduxStore/accessLogs/slice';
import { appActions } from 'reduxStore/app/slice';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { bookingActions } from 'reduxStore/bookings/slice';
import { categoriesActions } from 'reduxStore/categories/slice';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { devicesActions } from 'reduxStore/devices/slice';
import { lockersLogsActions } from 'reduxStore/lockersLogs/slice';
import { peopleActions } from 'reduxStore/people/slice';
import { permissionsActions } from 'reduxStore/permissions/slice';
import { settingsActions } from 'reduxStore/settings/slice';
import { typesActions } from 'reduxStore/types/slice';
import { io } from 'socket.io-client';
import { Notification } from 'zComponents/atoms';

const SocketManager = () => {
	const dispatch = useDispatch();
	const permissions = useSelector((state) => state.Auth.userPermissions);
	const { fBPersonID, orgID } = useSelector((state) => state.Auth.selectedProfile);

	// 1. Create a safe, primitive dependency string.
	// This prevents Redux array mutations from unnecessarily restarting the socket.
	const permissionsKey = permissions
		.map((p) => p.orgID)
		.sort()
		.join(',');

	// 2. Keep orgID in a Ref.
	// This allows event listeners to always access the latest orgID without restarting the connection.
	const orgIdRef = useRef(orgID);
	useEffect(() => {
		orgIdRef.current = orgID;
	}, [orgID]);

	useEffect(() => {
		if (!permissionsKey || !fBPersonID) return;

		// 3. ESLINT WARNING FIX:
		// We build the rooms array by splitting the permissionsKey string.
		// This avoids passing the entire 'permissions' array as a dependency.
		const rooms = permissionsKey
			.split(',')
			.filter(Boolean)
			.map((id) => `org:${id}`);

		rooms.push(`person:${fBPersonID}`);

		const payload = { rooms };

		// Create the SINGLE WebSocket connection for the entire app
		const socket = io(socketURLGenerator(), {
			query: { data: JSON.stringify(payload) }
		});

		// ==========================================
		// EVENT REGISTRATION (Bound ONLY ONCE)
		// ==========================================
		socket.on(SOCKET_EVENTS.ADD_DEVICE_EVENT, () => Notification('success', 'New device has been added'));

		socket.on(SOCKET_EVENTS.GRANTED_PERMISSION, () => {
			dispatch(permissionsActions.loadPermissions());
			Notification('success', 'Access Granted');
		});

		socket.on(SOCKET_EVENTS.REVOKED_PERMISSION, () => {
			dispatch(permissionsActions.loadPermissions());
			Notification('success', 'Access Revoked');
		});

		socket.on(SOCKET_EVENTS.SETTING_ENABLE_ACCESS_CONTROL, () => {
			dispatch(settingsActions.loadOrgSettings());
			Notification('success', 'Authorization granted');
		});

		socket.on(SOCKET_EVENTS.SETTING_DISABLE_ACCESS_CONTROL, () => {
			dispatch(settingsActions.loadOrgSettings());
			Notification('success', 'Authorization revoked');
		});

		socket.on(SOCKET_EVENTS.LOG_ACCESS_EVENT, () => {
			dispatch(accessLogsActions.loadAccessLogs({ orgID: orgIdRef.current }));
			dispatch(lockersLogsActions.loadLockersLogs());
		});

		socket.on(SOCKET_EVENTS.LOG_SYNC_COMPLETE, () => {
			dispatch(accessLogsActions.loadAccessLogs({ orgID: orgIdRef.current }));
		});

		// --- ORG-SPECIFIC EVENTS ---
		socket.on(SOCKET_EVENTS.LOCKER_BOOKING_CREATED, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(bookingActions.loadBookings());
				Notification('success', 'New booking created');
			}
		});

		socket.on(SOCKET_EVENTS.LOCKER_BOOKED_BY_PERSON, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(bookingActions.loadBookings());
				Notification('success', 'New booking created');
			}
		});

		socket.on(SOCKET_EVENTS.LOCKER_BOOKING_DELETED, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(bookingActions.loadBookings());
				Notification('success', 'Booking deleted');
			}
		});

		socket.on(SOCKET_EVENTS.LOCKER_RELEASED_BY_PERSON, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(bookingActions.loadBookings());
				Notification('success', 'Booking released');
			}
		});

		// --- CRITICAL SYNC EVENTS ---
		socket.on(SOCKET_EVENTS.SYNC_PROGRESS, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(appActions.setSyncStep(data.label));
			}
		});

		socket.on(SOCKET_EVENTS.SYNC_COMPLETE, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(appActions.clearSyncStep());
				dispatch(settingsActions.loadOrgSettings());

				if (data?.integrationID === INTEGRATIONS_TYPES.INTEGRATION_INNER_RANGE.id) {
					dispatch(categoriesActions.loadCategories());
					dispatch(typesActions.loadCardTemplatesTypes());
					dispatch(credentialsActions.loadCredentials());
					dispatch(assetGroupActions.loadGroups());
					dispatch(devicesActions.loadDevices());
					dispatch(peopleActions.loadPerson());
					dispatch(permissionsActions.loadPermissions());
					Notification('success', 'Inner Range sync completed successfully.');
				}

				if (data?.integrationID === INTEGRATIONS_TYPES.INTEGRATION_CONTROL.id) {
					dispatch(categoriesActions.loadCategories());
					dispatch(devicesActions.loadDevices());
					dispatch(credentialsActions.loadCredentials());
					dispatch(credentialsActions.getCredentialAccess());
					Notification('success', 'Control sync completed successfully.');
				}
			}
		});

		socket.on(SOCKET_EVENTS.SYNC_ERROR, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(appActions.clearSyncStep());
				dispatch(settingsActions.loadOrgSettings());

				if (data?.integrationID === INTEGRATIONS_TYPES.INTEGRATION_INNER_RANGE.id) {
					Notification('error', 'Inner Range sync failed. Please try again.');
				} else if (data?.integrationID === INTEGRATIONS_TYPES.INTEGRATION_CONTROL.id) {
					Notification('error', 'Control sync failed. Please try again.');
				} else {
					Notification('error', 'Sync failed. Please try again.');
				}
			}
		});

		// --- BULK CREDENTIAL CREATION EVENTS ---
		socket.on(SOCKET_EVENTS.CREDENTIAL_BULK_PROGRESS, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(
					appActions.setBulkCredentialProgress({
						processed: data.processed,
						total: data.total,
						lastCard: data.lastCard || null
					})
				);
			}
		});

		socket.on(SOCKET_EVENTS.CREDENTIAL_BULK_COMPLETE, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(
					appActions.setBulkCredentialProgress({
						complete: true,
						total: data.total,
						succeeded: data.succeeded,
						failed: data.failed || []
					})
				);
				dispatch(credentialsActions.loadCredentials());
				dispatch(credentialsActions.getCredentialAccess());
			}
		});

		socket.on(SOCKET_EVENTS.CREDENTIAL_BULK_ERROR, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(appActions.clearBulkCredentialProgress());
			}
		});

		// --- CREDENTIAL PROPAGATION EVENTS ---
		socket.on(SOCKET_EVENTS.CREDENTIAL_PROPAGATION_QUEUED, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(credentialsActions.credentialPropagationQueued(data));
			}
		});

		socket.on(SOCKET_EVENTS.CREDENTIAL_PROPAGATION_STARTED, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(credentialsActions.credentialPropagationStarted(data));
			}
		});

		socket.on(SOCKET_EVENTS.CREDENTIAL_PROPAGATION_PROGRESS, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(credentialsActions.credentialPropagationProgress(data));
			}
		});

		socket.on(SOCKET_EVENTS.CREDENTIAL_PROPAGATION_COMPLETE, (data) => {
			if (data?.orgID === orgIdRef.current) {
				dispatch(credentialsActions.credentialPropagationComplete(data));
				// Silent refetch
				dispatch(credentialsActions.loadCredentials());
				dispatch(credentialsActions.getCredentialAccess());
				// Refresh DB propagation state (failed rows feed the topbar Pending tab)
				dispatch(credentialsActions.fetchPropagation());

				const credentialID = data.credentialID;

				if (data.failed > 0) {
					// Keep propagation visible for retry — do NOT auto-clear
					Notification('warning', `Credential not propagated to ${data.failed} lock(s).`);
				} else {
					// All success — auto-clear after 5s
					if (!window._propagationTimeouts) window._propagationTimeouts = {};
					if (window._propagationTimeouts[credentialID]) {
						clearTimeout(window._propagationTimeouts[credentialID]);
					}
					window._propagationTimeouts[credentialID] = setTimeout(() => {
						dispatch(credentialsActions.clearCredentialPropagation({ credentialID }));
						delete window._propagationTimeouts[credentialID];
					}, 5000);
				}
			}
		});

		// ==========================================
		// TOTAL CLEANUP: Disconnect when the user logs out or app unmounts
		// ==========================================
		return () => {
			socket.disconnect();
		};
	}, [permissionsKey, fBPersonID, dispatch]);

	return <div />;
};

export default SocketManager;
